/**
 * @module EmptyNotifications
 * @package @geenius/notifications-solidjs
 * @description Renders the empty-state used when there are no notifications to
 * display in the SolidJS Tailwind variant.
 */

import { FadeIn } from '@geenius/motion/solidjs'
import type { JSX } from 'solid-js'
import { createNotificationCopy } from '../lib/notificationCopy'
import { cn } from '../lib/utils'

/**
 * Renders the empty notification state.
 *
 * @returns The empty-state markup.
 */
export interface EmptyNotificationsProps {
  class?: string
}

/**
 * Renders the empty notification state.
 *
 * @param props Optional style override for the empty-state container.
 * @returns The empty-state markup.
 */
export function EmptyNotifications(props: EmptyNotificationsProps): JSX.Element {
  const { t } = createNotificationCopy()

  return (
    <FadeIn
      class={cn(
        'flex flex-col items-center justify-center px-4 py-16 text-center',
        props.class,
      )}
    >
      <span aria-hidden="true" class="mb-3 text-5xl opacity-20">
        🔔
      </span>
      <div class="mb-1 text-[15px] font-medium text-foreground">
        {t('notifications.empty.title')}
      </div>
      <div class="text-xs text-muted-foreground">
        {t('notifications.empty.description')}
      </div>
    </FadeIn>
  )
}
